import type { StudioBridge } from '@/types/render';
import { AnalyticsEvent, trackEvent } from '@/shared/lib/analytics';
import { toast } from '@/shared/lib/toast';

export type StudioBridgePlacement = 'menu' | 'after_save' | 'notice';

export function openStudioBridge(
  bridge: StudioBridge,
  renderId: string,
  placement: StudioBridgePlacement
): void {
  trackEvent(AnalyticsEvent.STUDIO_BRIDGE_TAPPED, { render_id: renderId, placement });
  window.open(bridge.url, '_blank', 'noopener,noreferrer');
}

/**
 * After the user saved or shared a render they have picked a design, which is
 * the moment the studio offer makes sense. A non-modal toast with the server's
 * copy; it never interrupts work and disappears on its own.
 */
// Pacing for the after-save toast, same values on Apple and Android: people
// save several designs in a row, and the same offer on every save turns into
// noise. Per device; the menu entry and the notice stay available regardless.
const AFTER_SAVE_KEY = 'studio_bridge_after_save';
const AFTER_SAVE_MAX_TIMES = 3;
const AFTER_SAVE_MIN_GAP_MS = 7 * 24 * 60 * 60 * 1000;

function claimAfterSaveSlot(): boolean {
  try {
    const raw = localStorage.getItem(AFTER_SAVE_KEY);
    const seen = raw ? (JSON.parse(raw) as { count: number; last: number }) : { count: 0, last: 0 };
    if (seen.count >= AFTER_SAVE_MAX_TIMES || Date.now() - seen.last < AFTER_SAVE_MIN_GAP_MS) {
      return false;
    }
    localStorage.setItem(AFTER_SAVE_KEY, JSON.stringify({ count: seen.count + 1, last: Date.now() }));
    return true;
  } catch {
    // Storage blocked (private mode): skip the toast rather than show it on every save.
    return false;
  }
}

export function offerStudioBridgeAfterSave(
  bridge: StudioBridge | null | undefined,
  renderId: string | null | undefined
): void {
  if (!bridge || !renderId || !claimAfterSaveSlot()) return;
  trackEvent(AnalyticsEvent.STUDIO_BRIDGE_SHOWN, { render_id: renderId, placement: 'after_save' });
  // Same tappable toast as job completions (whole body is the tap target):
  // no extra button label to localize, the server copy is the call to action.
  toast.completion(bridge.title, {
    description: bridge.caption,
    onTap: () => openStudioBridge(bridge, renderId, 'after_save'),
  });
}
